<template>
  <div class="order-modify-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="back-btn" @click="toBack">
          <i class="el-icon-arrow-left"></i>
          <span>返回</span>
        </div>
        <div class="page-title">订单修改</div>
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="page-content">
      <!-- 订单基本信息 -->
      <div class="order-basic-info">
        <div class="info-card">
          <div class="info-header">
            <div class="order-code">订单号：{{ orderInfo.orderCode }}</div>
            <div class="submit-info">
              <span class="submit-by">提交人：{{ orderInfo.submitByName }}</span>
              <span class="submit-time">提交时间：{{ orderInfo.submitAt }}</span>
            </div>
          </div>
          <div class="info-footer">
            <span class="buyer-name" v-if="orderInfo.buyerName">买家名称：{{ orderInfo.buyerName }}</span>
          </div>
        </div>
      </div>

      <!-- 收货地址 -->
      <div class="address-section">
        <div class="section-card">
          <div class="card-header">
            <div class="card-title">
              <i class="el-icon-location-information"></i>
              <span>收货地址</span>
            </div>
          </div>
          <div class="card-content">
            <EditAddress @select="selectAddress" :addressInfo="addressInfo" />
          </div>
        </div>
      </div>

      <!-- 商品明细 -->
      <div class="goods-section">
        <div class="section-card">
          <div class="card-header">
            <div class="card-title">
              <i class="el-icon-shopping-bag-1"></i>
              <span>商品明细</span>
            </div>
          </div>
          <div class="card-content">
            <OrderDetailItem :table="goodsDetailsTable" :isShow="false">
              <template #footer>
                <div class="goods-summary">
                  <div class="summary-row">
                    <span>共{{ goodsDetailsTable.length }}种，{{ orderInfo.totalQty }}件商品</span>
                    <span class="total-amount">合计：{{ finalAmountFn(orderInfo) }}</span>
                  </div>
                  <div class="summary-row discount-info" v-if="Number(orderInfo.concessionsAmount) > 0">
                    <span class="original-price">
                      原价：{{ Number(orderInfo.finalOriginalPrice) ? toFixedFn(orderInfo.finalOriginalPrice) : 0 }}
                    </span>
                    <span class="discount-amount">已优惠：{{ toFixedFn(orderInfo.concessionsAmount) }}</span>
                  </div>
                </div>
              </template>
            </OrderDetailItem>
          </div>
        </div>
      </div>

      <!-- 订单信息 -->
      <div class="order-info-section">
        <div class="section-card">
          <div class="card-header">
            <div class="card-title">
              <i class="el-icon-document"></i>
              <span>订单信息</span>
            </div>
          </div>
          <div class="card-content">
            <div class="order-info-content">
              <div class="info-column">
                <div class="info-item">
                  <span class="info-label">结算方式：</span>
                  <span class="info-value">{{ orderInfo.paymentType }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">发票类型：</span>
                  <span class="info-value">{{ orderInfo.invoiceType }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">提货方式：</span>
                  <span class="info-value">{{ orderInfo.deliveryType }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">运输方式：</span>
                  <span class="info-value">{{ orderInfo.transportType }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">发货门店：</span>
                  <span class="info-value">{{ orderInfo.realSellerName }}/{{ orderInfo.realSellerPhone }}</span>
                </div>
                <div class="info-item" v-if="orderInfo.splitBy">
                  <span class="info-label">拆单类型：</span>
                  <span class="info-value">{{ orderInfo.splitBy }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">创建人：</span>
                  <span class="info-value">{{ orderInfo.createdByName }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">创建时间：</span>
                  <span class="info-value">{{ orderInfo.createdAt }}</span>
                </div>
              </div>
              <div class="remarks-column">
                <div class="remarks-label">买家备注：</div>
                <el-input
                  type="textarea"
                  class="remarks-input"
                  placeholder="请输入内容"
                  v-model="orderInfo.remarks"
                  maxlength="200"
                  clearable
                  show-word-limit
                  :autosize="{ minRows: 5, maxRows: 5 }"
                >
                </el-input>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 价格汇总 -->
      <div class="price-summary">
        <div class="summary-card">
          <div class="summary-content">
            <div class="summary-item">
              <span class="summary-label">商品总价</span>
              <span class="summary-value">{{ toFixedFn(orderInfo.totalAmount) }}</span>
            </div>
            <div v-if="couponInfo && couponInfo.id" class="summary-item">
              <span class="summary-label">优惠券</span>
              <span class="summary-value">
                <span class="coupon-tag">{{ couponInfo.content }}</span>
                <span class="coupon-amount">-¥{{ Number(couponInfo.amount).toFixed(2) }}</span>
              </span>
            </div>
            <div v-if="pointsData && pointsData.enable" class="summary-item">
              <span class="summary-label">积分抵扣</span>
              <span class="summary-value" v-if="pointsData.availablePoints">
                本次使用{{ pointsData.availablePoints }}积分抵扣{{ pointsData.availableDeductAmount }}元
              </span>
              <span v-else class="summary-value">暂无可用积分</span>
            </div>
            <div v-if="pointsData && pointsData.enable && pointsData.incomePoints" class="summary-item">
              <span class="summary-label">
                <span class="gift-tag">积分</span>
              </span>
              <span class="summary-value">订单完成后积分 +{{ pointsData.incomePoints }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">应付金额</span>
              <span class="summary-value">{{ toFixedFn(orderInfo.payAmount) }}</span>
            </div>
            <div class="summary-item total-pay">
              <span class="summary-label">实付金额</span>
              <span class="summary-value total-amount">{{ toFixedFn(orderInfo.paidAmount) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页面底部 -->
    <div class="page-footer">
      <div class="footer-actions">
        <el-button type="primary" class="confirm-btn" size="small" @click="sureFn">
          确定(F4)
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import Card from "../components/Card.vue";
import EditAddress from "../components/EditAddress.vue";
import OrderDetailItem from "../components/OrderDetailItem.vue";

import { getOrderDetail, updateOrders } from "@/api/order";

export default {
  name: "modifyOrderDetail1", //待发货、待确认 修改订单详情
  components: {
    Card,
    EditAddress,
    OrderDetailItem
  },
  data() {
    return {
      keyName: "f4",
      keyScope: "ModifyOrderDetail1",
      // 订单详情
      orderInfo: {},
      addressInfo: {},
      // 商品明细表
      goodsDetailsTable: [],
      // 优惠券，积分
      couponList: [],
      couponInfo: {},
      pointsData: {
        availableDeductAmount: 0,
        availablePoints: 0,
        points: 0,
        incomePoints: 0,
        enable: true,
        isDetail: false
      }
    };
  },
  mounted() {
    this.getOrderDetailFn();

    setTimeout(() => {
      this.$hotkeys(this.keyName, this.keyScope, this.handleKeyCode);
      this.$hotkeys.setScope(this.keyScope);
    }, 500);
  },
  beforeDestroy() {
    this.$hotkeys.unbind(this.keyName, this.keyScope);
  },
  methods: {
    finalAmountFn(item) {
      return Number(item.finalOriginalPrice) > 0
        ? (Number(item.finalOriginalPrice) - Number(item.concessionsAmount)).toFixed(2)
        : 0;
    },
    handleKeyCode(e) {
      switch (e.code) {
        case "F4":
          e.preventDefault();
          e.stopPropagation();
          this.sureFn();
          break;
      }
    },
    //金额保留两位小数
    toFixedFn(amount) {
      if (amount === "0" || Number(amount) === 0) {
        return "- -";
      }
      return isNaN(Number(amount)) ? "议价" : `￥${Number(amount).toFixed(2)}`;
    },
    //获取订单详情
    getOrderDetailFn() {
      getOrderDetail({ id: this.$route.query.id })
        .then(res => {
          this.orderInfo = res || {};
          this.addressInfo = {
            type: "modify",
            receiver: res.receiver,
            receiverAddress: res.receiverAddress,
            receiverAreaId: res.receiverAreaId,
            receiverPhone: res.receiverPhone,
            receiverAreaName: res.receiverAreaName
          };
          this.goodsDetailsTable =
            res.details.map(item => ({
              ...item,
              amount: (Number(item.qty) * Number(item.price)).toFixed(2)
            })) || [];

          if (res.concessions && res.concessions.coupon) {
            this.couponInfo = res.concessions.coupon;
          }
          this.pointsData = {
            availableDeductAmount: Number(res.concessions?.point?.amount || 0),
            availablePoints: Number(res.concessions?.point?.points || 0),
            points: Number(res.points || 0),
            incomePoints: Number(res.incomePoints || 0),
            enable: res.enable,
            isDetail: true
          };
        })
        .catch(err => {
          this.$message.error(err?.message);
        });
    },
    //返回
    toBack() {
      this.$router.go(-1);
    },
    //选择地址
    selectAddress(data) {
      this.orderInfo.addressId = data.id;
    },
    //确定修改
    sureFn() {
      const params = {
        ...this.orderInfo
      };
      updateOrders(params)
        .then(() => {
          this.$message.success("订单修改成功");
          this.$router.go(-1);
        })
        .catch(err => {
          this.$message.error(err?.message);
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.order-modify-page {
  background-color: #f5f7fa;
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  .page-header {
    background: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border-bottom: 1px solid #e6e8eb;

    .header-content {
      display: flex;
      align-items: center;
      padding: 12px 20px;

      .back-btn {
        display: flex;
        align-items: center;
        color: #606266;
        cursor: pointer;
        transition: color 0.3s;

        &:hover {
          color: var(--color-primary);
        }

        i {
          margin-right: 4px;
        }
      }

      .page-title {
        flex: 1;
        text-align: center;
        font-size: 18px;
        font-weight: 600;
        color: #303133;
      }
    }
  }

  .page-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
  }

  .order-basic-info {
    margin-bottom: 20px;

    .info-card {
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .info-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        .order-code {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
        }

        .submit-info {
          color: #606266;
          font-size: 14px;

          .submit-by {
            margin-right: 20px;
          }
        }
      }

      .info-footer {
        .buyer-name {
          color: #606266;
          font-size: 14px;
        }
      }
    }
  }

  .address-section,
  .goods-section,
  .order-info-section {
    margin-bottom: 20px;
  }

  .section-card {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .card-header {
      padding: 16px 20px;
      border-bottom: 1px solid #f0f2f5;

      .card-title {
        display: flex;
        align-items: center;
        font-size: 16px;
        font-weight: 600;
        color: #303133;

        i {
          margin-right: 8px;
          color: var(--color-primary);
        }
      }
    }

    .card-content {
      padding: 20px;
    }
  }

  .goods-summary {
    padding-top: 16px;
    border-top: 1px solid #f0f2f5;

    .summary-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;

      .total-amount {
        font-weight: 600;
        color: #ff6b6b;
      }

      &.discount-info {
        font-size: 14px;

        .original-price {
          color: #909399;
          text-decoration: line-through;
        }

        .discount-amount {
          color: #67c23a;
        }
      }
    }
  }

  .order-info-content {
    display: flex;

    .info-column {
      flex: 1;
      padding-right: 20px;

      .info-item {
        display: flex;
        align-items: center;
        margin-bottom: 16px;

        .info-label {
          width: 80px;
          font-weight: 500;
          color: #606266;
        }

        .info-value {
          color: #303133;
        }
      }
    }

    .remarks-column {
      flex: 1;

      .remarks-label {
        font-weight: 500;
        color: #606266;
        margin-bottom: 8px;
      }

      .remarks-input {
        ::v-deep .el-textarea__inner {
          border-radius: 4px;
          resize: none;
        }
      }
    }
  }

  .price-summary {
    margin-bottom: 20px;

    .summary-card {
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .summary-content {
        width: 300px;
        margin-left: auto;

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;

          .summary-label {
            color: #606266;
          }

          .summary-value {
            color: #303133;

            .coupon-tag {
              background: rgba(255, 107, 107, 0.1);
              color: #ff6b6b;
              border: 1px solid rgba(255, 107, 107, 0.3);
              border-radius: 2px;
              padding: 1px 4px;
              font-size: 11px;
              margin-right: 6px;
            }

            .coupon-amount {
              color: #ff6b6b;
              font-weight: 500;
            }

            .gift-tag {
              background: rgba(64, 158, 255, 0.1);
              color: var(--color-primary);
              border: 1px solid rgba(64, 158, 255, 0.3);
              border-radius: 2px;
              padding: 1px 4px;
              font-size: 11px;
            }
          }

          &.total-pay {
            padding-top: 12px;
            border-top: 1px solid #f0f2f5;

            .summary-label {
              font-size: 16px;
              font-weight: 500;
            }

            .total-amount {
              font-size: 20px;
              font-weight: 600;
              color: #ff6b6b;
            }
          }
        }
      }
    }
  }

  .page-footer {
    background: #fff;
    border-top: 1px solid #e6e8eb;
    padding: 16px 20px;

    .footer-actions {
      display: flex;
      justify-content: flex-end;

      .confirm-btn {
        padding: 10px 24px;
        font-weight: 500;
      }
    }
  }
}

// 响应式适配
@media (max-width: 768px) {
  .order-modify-page {
    .page-content {
      padding: 12px;
    }

    .order-basic-info .info-card .info-header {
      flex-direction: column;
      align-items: flex-start;

      .submit-info {
        margin-top: 8px;
      }
    }

    .order-info-content {
      flex-direction: column;

      .info-column {
        padding-right: 0;
        margin-bottom: 20px;
      }
    }

    .price-summary .summary-card .summary-content {
      width: 100%;
    }
  }
}
</style>
